import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { RootState } from '../../store';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { usePresence } from '../../hooks/usePresence';
import { useCapabilities, shellBlockedKey } from '../../services/capabilities';
import LanguageSelect from '../common/LanguageSelect';
import ThemeToggle from '../common/ThemeToggle';
import FlowRulesDialog from '../workflow/canvas/FlowRulesDialog';

interface Props { open: boolean; onClose: () => void }

/**
 * 사용자 설정 — 사용자 메뉴의 「사용자 설정」. 이 사람에게만 적용되는 것을 한곳에 모은다.
 *   계정    이름·로그인 ID·메일, 관리자 여부, 외부명령어 권한(서버 판정)
 *   화면    언어·테마 (머리줄의 같은 단추와 같은 설정)
 *   편집    연결 규칙(잘못된 흐름을 막을지·물을지) — 로그인 ID 별로 이 브라우저에 저장된다
 */
const UserSettingsDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const user = useSelector((s: RootState) => s.auth.user) as any;
  const caps = useCapabilities();
  const [rulesOpen, setRulesOpen] = useState(false);
  const { mounted, shown } = usePresence(open, 200);
  useEscapeKey(() => { if (open && !rulesOpen) onClose(); });
  if (!mounted) return null;

  const shell = !caps ? '…'
    : caps.shell.allowed ? t('layout:userSettings.shellAll')
      : caps.shell.scripts ? t('layout:userSettings.shellScripts')
        : t(shellBlockedKey(caps) || 'workflow:toolbox.shellOff');
  const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-28 shrink-0 text-xs text-gray-500">{label}</div>
      <div className="flex-1 min-w-0 text-sm text-gray-800">{children}</div>
    </div>
  );

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center bg-black transition-opacity duration-200 ${shown ? 'bg-opacity-30' : 'bg-opacity-0'}`}
      onMouseDown={onClose}>
      <div className={`bg-white rounded-lg shadow-xl w-[520px] max-w-[95vw] transition-all duration-200 ${shown ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-2 scale-[0.98]'}`}
        onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="text-sm font-semibold text-gray-800">{t('layout:userSettings.title')}</div>
          <button type="button" className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
        </div>
        <div className="p-4">
          <div className="text-xs font-semibold text-gray-600 mb-1">{t('layout:userSettings.account')}</div>
          <Row label={t('layout:userSettings.name')}>{user?.name || user?.loginId || '-'}</Row>
          <Row label={t('layout:userSettings.loginId')}><span className="font-mono">{user?.loginId || user?.usrid || '-'}</span></Row>
          {user?.email && <Row label={t('layout:userSettings.email')}>{user.email}</Row>}
          <Row label={t('layout:userSettings.role')}>{caps?.admin ? t('layout:userSettings.admin') : t('layout:userSettings.member')}</Row>
          <Row label={t('layout:userSettings.shell')}><span className="text-xs text-gray-600">{shell}</span></Row>

          <div className="text-xs font-semibold text-gray-600 mt-4 mb-1">{t('layout:userSettings.screen')}</div>
          <Row label={t('layout:userSettings.language')}><LanguageSelect /></Row>
          <Row label={t('layout:userSettings.theme')}><ThemeToggle /></Row>

          <div className="text-xs font-semibold text-gray-600 mt-4 mb-1">{t('layout:userSettings.editing')}</div>
          <Row label={t('layout:userSettings.flowRules')}>
            <button type="button" className="px-2.5 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 text-gray-700" onClick={() => setRulesOpen(true)}>
              {t('layout:userSettings.flowRulesOpen')}
            </button>
            <div className="text-[0.6875rem] text-gray-500 mt-1">{t('layout:userSettings.flowRulesHint')}</div>
          </Row>
        </div>
        <div className="px-4 py-2 border-t border-gray-200 flex justify-end">
          <button type="button" className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white" onClick={onClose}>{t('common:ui.close')}</button>
        </div>
      </div>
      {rulesOpen && <div onMouseDown={(e) => e.stopPropagation()}><FlowRulesDialog onClose={() => setRulesOpen(false)} /></div>}
    </div>
  );
};

export default UserSettingsDialog;
